import React, { FC } from 'react';
import { useParams } from 'next/navigation';
import { Money } from 'shared/types/product';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import { Product, Variant } from 'types/entity/product';

interface ProductPriceProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  inModalVersion?: boolean;
  product?: Product;
  direction?: 'col' | 'row';
  showDiscountBadge?: boolean;
  fontSize?: string;
}

const ProductPrice: FC<ProductPriceProps> = ({
  variant,
  inModalVersion,
  className,
  direction,
  showDiscountBadge,
  fontSize,
  ...props
}) => {
  const { locale } = useParams();
  const discountPercentageCalc = (priceCent: number, discountedPriceCent: number | undefined) =>
    typeof discountedPriceCent !== 'undefined' &&
    (((priceCent as number) - (discountedPriceCent as number)) / (priceCent as number)) * 100;
  const priceCentAmount = variant?.price?.centAmount || 0;
  const price: Money = {
    centAmount: priceCentAmount,
    currencyCode: variant?.price?.currencyCode,
    fractionDigits: variant?.price?.fractionDigits ?? 0,
  };
  const discountedCentAmount = variant?.discountedPrice?.centAmount || undefined;

  const regularPriceDiscountPercent = discountPercentageCalc(priceCentAmount, discountedCentAmount);

  const regularPriceClassName = useClassNames([
    'block leading-loose',
    fontSize,
    { 'line-through text-gray-500': !!regularPriceDiscountPercent },
    { '2xl:text-14': !inModalVersion },
    { 'text-12': !!inModalVersion && !regularPriceDiscountPercent },
    { 'text-10': !!inModalVersion && !!regularPriceDiscountPercent },
  ]);

  const discountedPriceClassName = useClassNames([
    'block font-bold leading-loose',
    fontSize,
    { '2xl:text-14': !inModalVersion },
  ]);

  const discountPercentage =
    variant?.discountedPrice &&
    (((variant.price?.centAmount as number) - (discountedCentAmount as number)) /
      (variant.price?.centAmount as number)) *
      100;
  return (
    <div className={className} {...props} data-prod-ver-price-discount={discountPercentage}>
      {!!discountPercentage ? (
        <div className={`flex ${direction === 'col' ? 'flex-col' : 'flex-row justify-between'}`}>
          <div className={`flex ${direction === 'col' ? 'flex-col items-start' : 'flex-row items-center gap-8'}`}>
            <Typography className={discountedPriceClassName}>
              {CurrencyHelpers.formatForCurrency(variant?.discountedPrice as number, locale)}
            </Typography>
            <Typography className={regularPriceClassName}>
              {CurrencyHelpers.formatForCurrency(price, locale)}
            </Typography>
          </div>

          {showDiscountBadge && (
            <span className="ml-10 flex h-25 w-45 items-center justify-center bg-accent-red text-11 text-neutral-100">
              {Math.round(discountPercentage)}%
            </span>
          )}
        </div>
      ) : (
        <Typography className={`${discountedPriceClassName} text-start`}>
          {CurrencyHelpers.formatForCurrency(price, locale)}
        </Typography>
      )}
    </div>
  );
};

export default ProductPrice;
